import React, { useEffect, useState } from 'react';
import { Box, Heading, VStack, Text, HStack, Button, Card, CardBody, useToast, Checkbox, Select, Input, SimpleGrid } from '@chakra-ui/react';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../services/api';

const SettingsGeneral = () => {
  const { t } = useLanguage();
  const toast = useToast();

  const [availableCurrencies, setAvailableCurrencies] = useState(['EUR','USD','GBP']);
  const [selectedCurrencies, setSelectedCurrencies] = useState(['EUR','USD','GBP']);
  const [defaultCurrency, setDefaultCurrency] = useState('EUR');
  const [lowStockThreshold, setLowStockThreshold] = useState('');
  const [invoicePrefix, setInvoicePrefix] = useState('INV-');
  const [proposalValidityDays, setProposalValidityDays] = useState('30');
  const [defaultLanguage, setDefaultLanguage] = useState('de');
  const allCurrencies = ['EUR','USD','GBP'];
  const [companySettings, setCompanySettings] = useState({});
  const [savingCompany, setSavingCompany] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [availRes, defRes, lowStockRes, invPrefRes, validDaysRes, langRes] = await Promise.all([
          api.get('/api/settings/currency.available').catch(() => null),
          api.get('/api/settings/currency.default').catch(() => null),
          api.get('/api/settings/low_stock_threshold').catch(() => null),
          api.get('/api/settings/invoice_prefix').catch(() => null),
          api.get('/api/settings/proposal_validity_days').catch(() => null),
          api.get('/api/settings/language').catch(() => null)
        ]);
        if (availRes?.data?.data?.setting?.value) {
          try {
            const parsed = JSON.parse(availRes.data.data.setting.value);
            if (Array.isArray(parsed)) setSelectedCurrencies(parsed);
          } catch {}
        }
        if (defRes?.data?.data?.setting?.value) {
          setDefaultCurrency(defRes.data.data.setting.value);
        }
        // Load company_* settings
        try {
          const comp = await api.get('/api/settings/by-prefix/company_');
          const map = {};
          (comp?.data?.data?.settings || []).forEach(s => { map[s.key] = s.value || ''; });
          setCompanySettings(map);
        } catch {}
        if (lowStockRes?.data?.data?.setting?.value) setLowStockThreshold(lowStockRes.data.data.setting.value);
        if (invPrefRes?.data?.data?.setting?.value) setInvoicePrefix(invPrefRes.data.data.setting.value);
        if (validDaysRes?.data?.data?.setting?.value) setProposalValidityDays(validDaysRes.data.data.setting.value);
        if (langRes?.data?.data?.setting?.value) setDefaultLanguage(langRes.data.data.setting.value);
      } catch {}
    };
    load();
  }, []);

  const companyFields = [
    { key: 'company_name', label: t('settings.company_name') },
    { key: 'company_address', label: t('settings.address') },
    { key: 'company_postcode', label: t('settings.postcode') },
    { key: 'company_city', label: t('settings.city') },
    { key: 'company_country', label: t('settings.country') },
    { key: 'company_phone', label: t('settings.phone') },
    { key: 'company_email', label: t('settings.email') },
    { key: 'company_website', label: t('settings.website') },
    { key: 'company_vat', label: t('settings.vat_number') },
  ];

  const handleSaveCompany = async () => {
    try {
      setSavingCompany(true);
      await Promise.all(companyFields.map(async (f) => {
        const val = companySettings[f.key] || '';
        await api.put(`/api/settings/${f.key}`, { value: val, description: f.label });
      }));
      toast.closeAll?.();
      toast({ title: t('common.success'), description: t('settings.updated_success'), status: 'success' });
    } catch (e) {
      toast({ title: t('common.error'), description: t('messages.server_error'), status: 'error' });
    } finally {
      setSavingCompany(false);
    }
  };

  const handleToggle = (code) => {
    setSelectedCurrencies((prev) => {
      if (prev.includes(code)) return prev.filter(c => c !== code);
      return [...prev, code];
    });
  };

  const handleSaveCurrencies = async () => {
    try {
      // default, available içinde değilse ekle
      const toSave = selectedCurrencies.includes(defaultCurrency)
        ? selectedCurrencies
        : [...selectedCurrencies, defaultCurrency];

      await api.put('/api/settings/currency.available', { value: JSON.stringify(toSave), description: 'Available currency codes' });
      await api.put('/api/settings/currency.default', { value: defaultCurrency, description: 'Default system currency' });
      toast({ title: t('common.success'), description: t('settings.updated_success'), status: 'success' });
    } catch (e) {
      toast({ title: t('common.error'), description: t('messages.server_error'), status: 'error' });
    }
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>{t('settings.general_title')}</Heading>
      <VStack spacing={6} align="stretch">
        <Card>
          <CardBody>
            <VStack align="start" spacing={4}>
              <Heading size="md">{t('settings.company_information')}</Heading>
              <Text fontSize="sm" color="gray.600">{t('settings.company_help')}</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} w="100%">
                {companyFields.map(f => (
                  <Box key={f.key}>
                    <Text fontSize="sm" color="gray.700" mb={1}>{f.label}</Text>
                    <Input size="sm" value={companySettings[f.key] || ''} onChange={(e)=> setCompanySettings(prev => ({ ...prev, [f.key]: e.target.value }))} />
                  </Box>
                ))}
              </SimpleGrid>
              <HStack>
                <Button colorScheme="brand" onClick={handleSaveCompany} isLoading={savingCompany}>{t('common.save')}</Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <VStack align="start" spacing={4}>
              <Heading size="md">{t('settings.general_title')}</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} w="100%">
                <Box>
                  <Text fontSize="sm" color="gray.700" mb={1}>{t('settings.low_stock_threshold')}</Text>
                  <Input size="sm" value={lowStockThreshold} onChange={(e)=> setLowStockThreshold(e.target.value)} />
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.700" mb={1}>{t('settings.invoice_prefix')}</Text>
                  <Input size="sm" value={invoicePrefix} onChange={(e)=> setInvoicePrefix(e.target.value)} />
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.700" mb={1}>{t('settings.proposal_validity_days')}</Text>
                  <Input size="sm" value={proposalValidityDays} onChange={(e)=> setProposalValidityDays(e.target.value)} />
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.700" mb={1}>{t('settings.default_language')}</Text>
                  <Select size="sm" value={defaultLanguage} onChange={(e)=> setDefaultLanguage(e.target.value)}>
                    <option value="de">{t('settings.language_german')}</option>
                    <option value="en">{t('settings.language_english')}</option>
                  </Select>
                </Box>
              </SimpleGrid>
              <HStack>
                <Button size="sm" colorScheme="brand" onClick={async ()=>{
                  try {
                    await Promise.all([
                      api.put('/api/settings/low_stock_threshold', { value: lowStockThreshold, description: 'Low stock item threshold' }),
                      api.put('/api/settings/invoice_prefix', { value: invoicePrefix, description: 'Invoice number prefix' }),
                      api.put('/api/settings/proposal_validity_days', { value: proposalValidityDays, description: 'Proposal validity in days' }),
                      api.put('/api/settings/language', { value: defaultLanguage, description: 'Default language' }),
                    ]);
                    toast({ title: t('common.success'), description: t('settings.updated_success'), status: 'success' });
                  } catch {
                    toast({ title: t('common.error'), description: t('messages.server_error'), status: 'error' });
                  }
                }}>{t('common.save')}</Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <VStack align="start" spacing={4}>
              <Heading size="md">{t('settings.currency_settings')}</Heading>
              <Text fontSize="sm" color="gray.600">{t('settings.currency_help')}</Text>
              <VStack align="start" spacing={2}>
                <Text fontWeight="semibold">{t('settings.available_currencies')}</Text>
                <HStack>
                  {allCurrencies.map(code => (
                    <Checkbox key={code} isChecked={selectedCurrencies.includes(code)} onChange={() => handleToggle(code)}>
                      {code}
                    </Checkbox>
                  ))}
                </HStack>
              </VStack>
              <VStack align="start" spacing={2}>
                <Text fontWeight="semibold">{t('settings.default_currency')}</Text>
                <Select size="sm" value={defaultCurrency} onChange={(e)=> setDefaultCurrency(e.target.value)} maxW="200px">
                  {(selectedCurrencies.length ? selectedCurrencies : allCurrencies).map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </Select>
              </VStack>
              <HStack>
                <Button colorScheme="brand" onClick={handleSaveCurrencies}>{t('common.save')}</Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <VStack align="start" spacing={4}>
              <Heading size="md">{t('settings.pdf_logo_title')}</Heading>
              <HStack>
                <Button as="label" colorScheme="purple" variant="outline">
                  {t('common.upload')} .png/.jpg
                  <input type="file" accept="image/*" hidden onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const formData = new FormData();
                    formData.append('file', file);
                    try {
                      const res = await api.post('/api/settings/branding/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                      toast({ title: t('common.success'), description: res.data.data?.url || t('settings.uploaded'), status: 'success' });
                    } catch (err) {
                      toast({ title: t('common.error'), description: t('messages.server_error'), status: 'error' });
                    }
                  }} />
                </Button>
              </HStack>
              <Text fontSize="sm" color="gray.600">{t('settings.pdf_logo_help_text')}</Text>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default SettingsGeneral;
